import * as THREE from 'three';
import { AssetLibrary, createProceduralSkyTexture } from '../assets/AssetLibrary';
import {
  FLOWER_META,
  FLOWER_ORDER,
  GAME,
  ZONE,
  salePrice,
  unlockedFlowers,
  type CarryKind,
  type FlowerKind,
  type UpgradeId,
} from '../config';
import { InputController } from '../core/InputController';
import { Loop } from '../core/Loop';
import { createRenderer, resizeRenderer } from '../core/Renderer';
import { StackCarrier, emptyCounts } from '../entities/StackCarrier';
import {
  createBeeMesh,
  createCashStackMesh,
  createCarryItemMesh,
  createCustomerMesh,
  createEmployeeMesh,
  createGuideArrow,
  createHoneyPotMesh,
  createOrderBubble,
  createPlayerMesh,
} from '../meshes/factory';
import { AudioSystem } from '../systems/AudioSystem';
import { CameraRig } from '../systems/CameraRig';
import { Hud } from '../systems/Hud';
import {
  canBuy,
  costFor,
  defaultLevels,
  deriveStats,
  effectPreview,
  loadSave,
  writeSave,
  type DerivedStats,
  type UpgradeLevels,
} from '../systems/Progression';
import { createSeededRandom, pick, type Rng } from '../utils/random';
import { World, type Plot } from './World';

type HoneyPot = { mesh: THREE.Group; position: THREE.Vector3; alive: boolean };
type CashPile = { mesh: THREE.Group; position: THREE.Vector3; value: number; alive: boolean };

type Bee = {
  mesh: THREE.Group;
  state: 'home' | 'toPlant' | 'planting' | 'return';
  targetPlot: Plot | null;
  plantKind: FlowerKind | null;
  timer: number;
  homeOffset: THREE.Vector3;
};

type Customer = {
  id: number;
  mesh: THREE.Group;
  bubble: THREE.Group;
  want: FlowerKind;
  state: 'enter' | 'queue' | 'served' | 'leave';
  queueIndex: number;
  position: THREE.Vector3;
};

type Employee = {
  mesh: THREE.Group;
  stack: StackCarrier;
  state: 'idle' | 'toStore' | 'toCustomer' | 'return';
  targetCustomer: Customer | null;
  position: THREE.Vector3;
};

export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(42, 1, 0.1, 160);
  private readonly input: InputController;
  private readonly audio = new AudioSystem();
  private readonly hud = new Hud();
  private readonly cameraRig = new CameraRig(this.camera);
  private readonly loop: Loop;
  private readonly guide = createGuideArrow();

  private rng: Rng = createSeededRandom(42);
  private world: World;
  private readonly player = new THREE.Group();
  private playerMesh: THREE.Object3D = createPlayerMesh();
  private readonly playerStack = new StackCarrier();
  private readonly moveInput = new THREE.Vector2();
  private readonly velocity = new THREE.Vector3();
  private facing = 0;
  private readonly assets = new AssetLibrary();
  private skyDome: THREE.Mesh | null = null;

  private readonly honeyPots: HoneyPot[] = [];
  private readonly cashPiles: CashPile[] = [];
  private readonly bees: Bee[] = [];
  private readonly customers: Customer[] = [];
  private employees: Employee[] = [];
  private readonly storeroom: CarryKind[] = [];
  private readonly storeVisual = new THREE.Group();

  private levels: UpgradeLevels = defaultLevels();
  private stats: DerivedStats = deriveStats(this.levels);
  private cash = 0;
  private milestoneIndex = 0;
  private customerSpawnTimer = 1.2;
  private honeySpawnTimer = 1;
  private nextCustomerId = 1;
  private playing = false;
  private tutorialStep = 0;
  private hiveCooldown = 0;
  private storeCooldown = 0;
  private saveTimer = 0;
  private frame = 0;
  private elapsed = 0;
  private pausedForScreenshot = false;
  private maxDpr = 2;
  private nearUpgrade = false;

  constructor(canvas: HTMLCanvasElement) {
    this.renderer = createRenderer(canvas);
    this.renderer.toneMappingExposure = 1.18;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.scene.background = new THREE.Color(0x7ec8e8);
    this.scene.fog = new THREE.FogExp2(0xb8d9ef, 0.018);

    const stick = mustEl('#touch-stick');
    const knob = mustEl('#touch-knob');
    this.input = new InputController(stick, knob);
    this.input.setEnabled(false);

    this.loadProgress();
    this.world = new World(this.rng);
    this.world.applyProgress(this.stats.fieldsLevel, this.stats.flowersLevel, this.shopVisualLevel());
    this.scene.add(this.world.root);

    this.setupSky();
    this.setupLights();
    this.setupPlayer();
    this.rebuildBees();
    this.rebuildEmployees();
    this.scene.add(this.storeVisual);
    this.storeVisual.position.copy(this.world.storeroomPos);
    this.scene.add(this.guide);

    this.loop = new Loop(
      (dt, elapsed) => this.update(dt, elapsed),
      () => this.render(),
    );

    this.cameraRig.snapTo(this.player.position);
    resizeRenderer(this.renderer, this.camera, this.maxDpr);
    this.hud.showTitle(true);
    this.hud.setBuyHandler((id) => this.purchaseUpgrade(id));
    this.hud.setPanelToggleHandler((open) => {
      if (!this.playing) return;
      this.input.setEnabled(!open);
      if (open) this.hud.renderUpgrades(this.levels, this.cash);
    });
    this.syncHud();
    this.bindUi();
    this.installTestHooks();
    this.publishDiagnostics();
  }

  /** Load Synty farm kit + polish materials (non-blocking for first frame). */
  async bootstrapArt(): Promise<void> {
    this.hud.setStatus('Loading farm art kit…');
    await this.assets.load();
    if (this.assets.hasAssets) {
      this.world.attachAssets(this.assets);
      this.upgradePlayerArt();
      this.rebuildEmployees();
      this.hud.setStatus('Synty farm art loaded · Grow · Harvest · Sell · Upgrade');
    } else {
      this.hud.setStatus('Procedural art mode · Grow · Harvest · Sell · Upgrade');
    }
    this.publishDiagnostics();
  }

  start(): void {
    this.loop.start();
    void this.bootstrapArt();
  }

  private setupSky(): void {
    const skyTex = createProceduralSkyTexture();
    this.scene.background = skyTex;
    this.scene.environment = skyTex;
    const geo = new THREE.SphereGeometry(80, 24, 16);
    const mat = new THREE.MeshBasicMaterial({ map: skyTex, side: THREE.BackSide, depthWrite: false });
    this.skyDome = new THREE.Mesh(geo, mat);
    this.scene.add(this.skyDome);
  }

  private upgradePlayerArt(): void {
    const hero = this.assets.clone('player');
    if (!hero) return;
    this.player.remove(this.playerMesh);
    // dispose old procedural mesh lightly
    this.playerMesh = hero;
    this.player.add(hero);
    // stack sits above shoulders
    this.playerStack.group.position.y = 0.2;
  }

  dispose(): void {
    this.loop.stop();
    this.input.dispose();
    this.audio.dispose();
    this.playerStack.dispose();
    this.renderer.dispose();
    window.__THREE_GAME_DIAGNOSTICS__ = undefined;
    window.__THREE_GAME_TEST_HOOKS__ = undefined;
  }

  private loadProgress(): void {
    const save = loadSave();
    if (!save) return;
    this.cash = save.cash;
    this.levels = { ...defaultLevels(), ...save.levels };
    this.milestoneIndex = save.milestoneIndex;
    this.stats = deriveStats(this.levels);
  }

  private persist(): void {
    writeSave({
      cash: Math.floor(this.cash),
      levels: this.levels,
      milestoneIndex: this.milestoneIndex,
      version: 2,
    });
  }

  private shopVisualLevel(): number {
    return Math.min(
      8,
      this.levels.fields + this.levels.clerk + Math.floor(this.levels.prices / 2) + this.levels.flowers,
    );
  }

  private bindUi(): void {
    mustEl('#start-button').addEventListener('click', () => this.beginPlay());
    window.addEventListener('keydown', (e) => {
      if (!this.playing) return;
      if (e.code === 'KeyU') {
        const open = !this.hud.isUpgradeOpen();
        this.hud.setUpgradeOpen(open);
        if (open) {
          this.hud.renderUpgrades(this.levels, this.cash);
          this.input.setEnabled(false);
        } else {
          this.input.setEnabled(true);
        }
      }
      if (e.code === 'Escape' && this.hud.isUpgradeOpen()) {
        this.hud.setUpgradeOpen(false);
        this.input.setEnabled(true);
      }
    });
  }

  private beginPlay(): void {
    this.playing = true;
    this.input.setEnabled(true);
    this.audio.resume();
    this.audio.play('click');
    this.hud.showTitle(false);
    this.hud.showTip('Harvest bushes · Sell at the counter · Cash piles appear on the green pad', 5);
    this.hud.setStatus('Grow · Harvest · Sell · Upgrade · Expand');
    this.tutorialStep = 1;
    this.applyStatsToActors();
  }

  private setupLights(): void {
    this.scene.add(new THREE.HemisphereLight(0xfff0d8, 0x3d6b4f, 0.95));
    const sun = new THREE.DirectionalLight(0xffe6c0, 1.55);
    sun.position.set(16, 24, 12);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 80;
    sun.shadow.camera.left = -30;
    sun.shadow.camera.right = 30;
    sun.shadow.camera.top = 30;
    sun.shadow.camera.bottom = -30;
    sun.shadow.bias = -0.0006;
    sun.shadow.normalBias = 0.02;
    this.scene.add(sun);
    const fill = new THREE.DirectionalLight(0xa8d8ff, 0.4);
    fill.position.set(-14, 10, -8);
    this.scene.add(fill);
    const rim = new THREE.DirectionalLight(0xffc9a0, 0.28);
    rim.position.set(-6, 6, 14);
    this.scene.add(rim);
  }

  private setupPlayer(): void {
    this.player.add(this.playerMesh);
    this.player.add(this.playerStack.group);
    this.player.position.set(ZONE.spawn.x, 0, ZONE.spawn.z);
    this.scene.add(this.player);
  }

  private rebuildBees(): void {
    for (const b of this.bees) this.scene.remove(b.mesh);
    this.bees.length = 0;
    for (let i = 0; i < this.stats.beeCount; i++) {
      const mesh = createBeeMesh();
      const homeOffset = new THREE.Vector3(
        Math.cos((i / this.stats.beeCount) * Math.PI * 2) * 0.85,
        0.95 + (i % 3) * 0.18,
        Math.sin((i / this.stats.beeCount) * Math.PI * 2) * 0.85,
      );
      mesh.position.copy(this.world.hivePos).add(homeOffset);
      this.scene.add(mesh);
      this.bees.push({ mesh, state: 'home', targetPlot: null, plantKind: null, timer: 0, homeOffset });
    }
  }

  private rebuildEmployees(): void {
    for (const e of this.employees) {
      e.stack.dispose();
      this.scene.remove(e.mesh);
    }
    this.employees = [];
    for (let i = 0; i < this.stats.clerkCount; i++) {
      const mesh = this.assets.clone('clerk') ?? createEmployeeMesh(i);
      const stack = new StackCarrier(this.stats.clerkCarry);
      mesh.add(stack.group);
      const position = this.world.storeroomPos.clone().add(new THREE.Vector3(0.7 + i * 0.5, 0, 1.0 + i * 0.35));
      mesh.position.copy(position);
      this.scene.add(mesh);
      this.employees.push({ mesh, stack, state: 'idle', targetCustomer: null, position });
    }
  }

  private applyStatsToActors(): void {
    this.stats = deriveStats(this.levels);
    this.playerStack.setMax(this.stats.stackMax);
    this.world.applyProgress(this.stats.fieldsLevel, this.stats.flowersLevel, this.shopVisualLevel());
    this.cameraRig.setWorldScale(this.stats.fieldsLevel);
    // rebuild bees/employees if counts changed
    if (this.bees.length !== this.stats.beeCount) this.rebuildBees();
    if (this.employees.length !== this.stats.clerkCount) this.rebuildEmployees();
    else {
      for (const e of this.employees) e.stack.setMax(this.stats.clerkCarry);
    }
    this.storeVisual.position.copy(this.world.storeroomPos);
  }

  private update(dt: number, elapsed: number): void {
    this.frame += 1;
    this.elapsed = elapsed;
    this.hud.update(dt);
    if (this.pausedForScreenshot) {
      this.publishDiagnostics();
      return;
    }

    this.world.update(dt);
    this.animateBeesHome(elapsed);

    if (!this.playing) {
      this.cameraRig.update(this.player.position, dt, 0.25);
      this.publishDiagnostics();
      return;
    }

    if (this.hud.isUpgradeOpen()) {
      this.cameraRig.update(this.player.position, dt, 0.2);
      this.publishDiagnostics();
      return;
    }

    this.updatePlayer(dt);
    this.updateHoney(dt);
    this.updateBees(dt);
    this.updateCustomers(dt);
    this.updateEmployees(dt);
    this.hiveCooldown = Math.max(0, this.hiveCooldown - dt);
    this.storeCooldown = Math.max(0, this.storeCooldown - dt);
    this.tryInteractions();
    this.updateGuide();
    this.playerStack.update(dt, this.velocity.lengthSq() > 0.05, this.facing);
    for (const e of this.employees) e.stack.update(dt, e.state !== 'idle', 0);
    this.cameraRig.update(this.player.position, dt, 0.14);
    this.syncHud();

    this.saveTimer += dt;
    if (this.saveTimer > 4) {
      this.saveTimer = 0;
      this.persist();
    }
    this.publishDiagnostics();
  }

  private updatePlayer(dt: number): void {
    this.input.readMovement(this.moveInput);
    const target = new THREE.Vector3(this.moveInput.x, 0, this.moveInput.y);
    if (target.lengthSq() > 0.001) {
      target.normalize().multiplyScalar(this.stats.speed);
      this.velocity.lerp(target, 1 - Math.exp(-12 * dt));
      this.facing = Math.atan2(this.velocity.x, this.velocity.z);
      this.player.rotation.y = this.facing;
    } else {
      this.velocity.multiplyScalar(Math.exp(-10 * dt));
    }
    this.player.position.addScaledVector(this.velocity, dt);
    this.world.clampPosition(this.player.position);
    // subtle bob on root for both procedural and Synty heroes
    const bob = Math.abs(Math.sin(this.elapsed * 10)) * (this.velocity.length() > 0.5 ? 0.04 : 0.01);
    this.playerMesh.position.y = bob;
    if (this.skyDome) this.skyDome.position.copy(this.player.position);
  }

  private updateHoney(dt: number): void {
    this.honeySpawnTimer -= dt;
    if (this.honeySpawnTimer <= 0) {
      this.honeySpawnTimer = this.stats.honeyInterval * (0.8 + this.rng() * 0.4);
      this.spawnHoneyPot();
    }
  }

  private spawnHoneyPot(): void {
    if (this.honeyPots.filter((h) => h.alive).length >= 6 + this.stats.beeCount) return;
    const mesh = createHoneyPotMesh();
    const ang = this.rng() * Math.PI * 2;
    const rad = 1.15 + this.rng() * 0.85;
    const position = new THREE.Vector3(
      this.world.hivePos.x + Math.cos(ang) * rad,
      0,
      this.world.hivePos.z + Math.sin(ang) * rad,
    );
    mesh.position.copy(position);
    mesh.position.y = 0.15;
    this.scene.add(mesh);
    this.honeyPots.push({ mesh, position, alive: true });
  }

  private spawnCashPile(value: number, near: THREE.Vector3): void {
    const mesh = createCashStackMesh(2 + Math.min(8, Math.floor(value / 4)));
    const position = near.clone().add(
      new THREE.Vector3((this.rng() - 0.5) * 0.8, 0, (this.rng() - 0.5) * 0.6),
    );
    // prefer money pad
    if (this.rng() < 0.65) {
      position.copy(this.world.moneyPad);
      position.x += (this.rng() - 0.5) * 1.2;
      position.z += (this.rng() - 0.5) * 0.8;
    }
    mesh.position.copy(position);
    this.scene.add(mesh);
    this.cashPiles.push({ mesh, position, value, alive: true });
  }

  private animateBeesHome(elapsed: number): void {
    for (let i = 0; i < this.bees.length; i++) {
      const bee = this.bees[i]!;
      if (bee.state !== 'home') continue;
      const base = this.world.hivePos.clone().add(bee.homeOffset);
      bee.mesh.position.x = base.x + Math.sin(elapsed * 2.5 + i) * 0.28;
      bee.mesh.position.y = base.y + Math.sin(elapsed * 4 + i * 1.3) * 0.16;
      bee.mesh.position.z = base.z + Math.cos(elapsed * 2.2 + i) * 0.28;
      const wings = bee.mesh.userData.wings as THREE.Object3D[] | undefined;
      if (wings) for (const w of wings) w.rotation.z = Math.sin(elapsed * 42 + i) * 0.5;
    }
  }

  private updateBees(dt: number): void {
    for (const bee of this.bees) {
      if (bee.state === 'home') continue;
      const wings = bee.mesh.userData.wings as THREE.Object3D[] | undefined;
      if (wings) for (const w of wings) w.rotation.z = Math.sin(this.elapsed * 48) * 0.6;

      if (bee.state === 'toPlant' && bee.targetPlot) {
        const dest = bee.targetPlot.position.clone();
        dest.y = 1.25;
        this.moveToward(bee.mesh.position, dest, GAME.plantFlightSpeed, dt);
        if (bee.mesh.position.distanceTo(dest) < 0.28) {
          bee.state = 'planting';
          bee.timer = 0.4;
        }
      } else if (bee.state === 'planting' && bee.targetPlot && bee.plantKind) {
        bee.timer -= dt;
        if (bee.timer <= 0) {
          const plot = bee.targetPlot;
          plot.kind = bee.plantKind;
          plot.ready = false;
          plot.growTimer = 4 + this.rng() * 2.5;
          plot.reserved = false;
          plot.mesh.visible = true;
          this.world.refreshPlotMesh(plot);
          this.audio.play('plant');
          this.flashStatus(`Bees planted ${FLOWER_META[bee.plantKind].label}s!`);
          bee.targetPlot = null;
          bee.plantKind = null;
          bee.state = 'return';
        }
      } else if (bee.state === 'return') {
        const dest = this.world.hivePos.clone().add(bee.homeOffset);
        this.moveToward(bee.mesh.position, dest, GAME.plantFlightSpeed, dt);
        if (bee.mesh.position.distanceTo(dest) < 0.22) bee.state = 'home';
      }
    }
  }

  private updateCustomers(dt: number): void {
    this.customerSpawnTimer -= dt;
    if (this.customerSpawnTimer <= 0 && this.queueCount() < this.stats.maxQueue) {
      this.customerSpawnTimer = this.stats.customerInterval * (0.75 + this.rng() * 0.5);
      this.spawnCustomer();
    }

    const waiting = this.customers
      .filter((c) => c.state === 'enter' || c.state === 'queue')
      .sort((a, b) => a.id - b.id);
    waiting.forEach((c, i) => {
      c.queueIndex = i;
    });

    for (const c of this.customers) {
      if (c.state === 'enter' || c.state === 'queue') {
        const slot = this.queueSlot(c.queueIndex);
        this.moveToward(c.position, slot, 2.9, dt);
        c.mesh.position.copy(c.position);
        c.bubble.position.copy(c.position);
        const dx = slot.x - c.position.x;
        const dz = slot.z - c.position.z;
        if (Math.hypot(dx, dz) > 0.05) c.mesh.rotation.y = Math.atan2(dx, dz);
        if (c.position.distanceTo(slot) < 0.15) c.state = 'queue';
      } else if (c.state === 'leave') {
        const exit = this.world.customerSpawn.clone();
        exit.x += (c.id % 5) - 2;
        this.moveToward(c.position, exit, 3.5, dt);
        c.mesh.position.copy(c.position);
        c.bubble.position.copy(c.position);
        c.bubble.visible = false;
        if (c.position.distanceTo(exit) < 0.5) c.state = 'served';
      }
    }

    for (let i = this.customers.length - 1; i >= 0; i--) {
      const c = this.customers[i]!;
      if (c.state !== 'served') continue;
      this.scene.remove(c.mesh);
      this.scene.remove(c.bubble);
      this.customers.splice(i, 1);
    }
  }

  private queueCount(): number {
    return this.customers.filter((c) => c.state === 'enter' || c.state === 'queue').length;
  }

  private queueSlot(index: number): THREE.Vector3 {
    return new THREE.Vector3(
      this.world.queueAnchor.x + (index % 2 === 0 ? -0.4 : 0.4),
      0,
      this.world.queueAnchor.z + index * 0.95,
    );
  }

  private spawnCustomer(): void {
    const kinds = unlockedFlowers(this.stats.flowersLevel);
    // Mix demand: mostly mid catalog, occasional premium when unlocked
    let want = pick(this.rng, kinds);
    const roll = this.rng();
    if (roll < 0.4) {
      const cheap = kinds.filter((k) => FLOWER_META[k].tier <= 2);
      if (cheap.length) want = pick(this.rng, cheap);
    } else if (roll > 0.82) {
      const premium = kinds.filter((k) => FLOWER_META[k].tier >= 3);
      if (premium.length) want = pick(this.rng, premium);
    }
    const mesh = this.assets.clone('customer') ?? createCustomerMesh(this.nextCustomerId);
    if (this.assets.hasAssets) mesh.rotation.y = this.rng() * Math.PI * 2;
    const bubble = createOrderBubble(want);
    const position = this.world.customerSpawn.clone();
    mesh.position.copy(position);
    bubble.position.copy(position);
    this.scene.add(mesh, bubble);
    this.customers.push({
      id: this.nextCustomerId++,
      mesh,
      bubble,
      want,
      state: 'enter',
      queueIndex: this.queueCount(),
      position,
    });
  }

  private updateEmployees(dt: number): void {
    for (const emp of this.employees) this.updateOneEmployee(emp, dt);
  }

  private updateOneEmployee(emp: Employee, dt: number): void {
    const speed = this.stats.clerkSpeed;
    if (emp.state === 'idle') {
      const target = this.customers.find(
        (c) =>
          (c.state === 'queue' || c.state === 'enter') &&
          this.storeroomIncludes(c.want) &&
          !this.employees.some((e) => e.targetCustomer === c && e !== emp),
      );
      if (target) {
        emp.targetCustomer = target;
        emp.state = 'toStore';
      } else {
        const home = this.world.storeroomPos.clone().add(new THREE.Vector3(0.9, 0, 1.1));
        this.moveToward(emp.position, home, speed * 0.45, dt);
        emp.mesh.position.copy(emp.position);
      }
      return;
    }

    if (emp.state === 'toStore') {
      const dest = this.world.storeroomPos.clone().add(new THREE.Vector3(0.4, 0, 0.6));
      this.moveToward(emp.position, dest, speed, dt);
      emp.mesh.position.copy(emp.position);
      this.face(emp.mesh, dest, emp.position);
      if (emp.position.distanceTo(dest) < 0.32 && emp.targetCustomer) {
        const kind = emp.targetCustomer.want;
        let loaded = 0;
        while (loaded < this.stats.clerkCarry && this.storeroomIncludes(kind)) {
          const idx = this.storeroom.indexOf(kind);
          if (idx < 0) break;
          this.storeroom.splice(idx, 1);
          emp.stack.add(kind);
          loaded += 1;
        }
        this.refreshStoreroomVisual();
        emp.state = loaded > 0 ? 'toCustomer' : 'idle';
        if (loaded === 0) emp.targetCustomer = null;
      }
      return;
    }

    if (emp.state === 'toCustomer' && emp.targetCustomer) {
      const c = emp.targetCustomer;
      if (c.state === 'leave' || c.state === 'served') {
        emp.state = 'return';
        emp.targetCustomer = null;
        return;
      }
      this.moveToward(emp.position, c.position, speed, dt);
      emp.mesh.position.copy(emp.position);
      this.face(emp.mesh, c.position, emp.position);
      if (emp.position.distanceTo(c.position) < 0.75) {
        if (emp.stack.take(c.want, 1) > 0) this.completeSale(c, 'clerk');
        const next = this.customers.find(
          (cust) =>
            (cust.state === 'queue' || cust.state === 'enter') && emp.stack.counts()[cust.want] > 0,
        );
        if (next) emp.targetCustomer = next;
        else if (emp.stack.count > 0) {
          emp.state = 'return';
          emp.targetCustomer = null;
        } else {
          emp.state = 'idle';
          emp.targetCustomer = null;
        }
      }
      return;
    }

    if (emp.state === 'return') {
      const dest = this.world.storeroomPos.clone().add(new THREE.Vector3(0.4, 0, 0.6));
      this.moveToward(emp.position, dest, speed, dt);
      emp.mesh.position.copy(emp.position);
      if (emp.position.distanceTo(dest) < 0.32) {
        for (const kind of emp.stack.takeTop(emp.stack.count)) {
          if (kind === 'honey' || kind === 'cash') continue;
          if (this.storeroom.length < this.stats.storeroomCap) this.storeroom.push(kind);
        }
        this.refreshStoreroomVisual();
        emp.state = 'idle';
      }
    }
  }

  private storeroomIncludes(kind: CarryKind): boolean {
    return this.storeroom.includes(kind);
  }

  private tryInteractions(): void {
    const p = this.player.position;

    // Harvest
    for (const plot of this.world.plots) {
      if (!plot.ready || plot.locked) continue;
      if (p.distanceTo(plot.position) > GAME.harvestRadius) continue;
      if (!this.playerStack.canAdd()) {
        this.flashStatus('Stack full! Sell, store, or upgrade Bigger Basket', true);
        break;
      }
      const kind = this.world.harvestPlot(plot);
      if (!kind) continue;
      this.playerStack.add(kind);
      this.audio.play('pickup');
      this.onTutorial(2, 'Sell at the pink counter — green pad holds cash piles');
      this.flashStatus(`Harvested ${FLOWER_META[kind].label}`);
      break;
    }

    // Honey
    for (const pot of this.honeyPots) {
      if (!pot.alive) continue;
      if (p.distanceTo(pot.position) > GAME.honeyPickupRadius) continue;
      if (!this.playerStack.canAdd()) continue;
      pot.alive = false;
      this.scene.remove(pot.mesh);
      this.playerStack.add('honey');
      this.audio.play('honey');
      this.onTutorial(3, 'Deposit honey at the hive to send bees planting');
      this.flashStatus('Honey collected');
    }

    // Cash piles
    for (const pile of this.cashPiles) {
      if (!pile.alive) continue;
      if (p.distanceTo(pile.position) > GAME.cashPickupRadius) continue;
      pile.alive = false;
      this.scene.remove(pile.mesh);
      this.cash += pile.value;
      this.audio.play('sell');
      this.hud.floatCash(pile.value);
      this.checkMilestones();
      this.flashStatus(`Collected $${pile.value}`);
      this.onTutorial(7, 'Visit the golden upgrade kiosk (or press U)');
    }

    // Hive honey deposit
    if (this.hiveCooldown <= 0 && p.distanceTo(this.world.hivePos) < GAME.depositRadius) {
      if (this.playerStack.counts().honey >= GAME.honeyPerPlant) {
        const freeBee = this.bees.find((b) => b.state === 'home');
        if (freeBee) {
          const kind = this.world.pickPlantKind(this.rng);
          const plot = this.world.plantAtEmpty(kind);
          if (plot) {
            plot.reserved = true;
            this.playerStack.take('honey', GAME.honeyPerPlant);
            freeBee.state = 'toPlant';
            freeBee.targetPlot = plot;
            freeBee.plantKind = kind;
            this.hiveCooldown = 0.32;
            this.audio.play('deposit');
            this.flashStatus(`Bee planting ${FLOWER_META[kind].label}s`);
            this.onTutorial(4, 'Stock the blue storeroom so clerks sell for you');
          }
        }
      }
    }

    // Storeroom
    if (this.storeCooldown <= 0 && p.distanceTo(this.world.storeroomPos) < GAME.depositRadius + 0.35) {
      const counts = this.playerStack.counts();
      const flowerCount = FLOWER_ORDER.reduce((s, k) => s + counts[k], 0);
      if (flowerCount > 0 && this.storeroom.length < this.stats.storeroomCap) {
        const space = this.stats.storeroomCap - this.storeroom.length;
        const batches = this.playerStack.takeAnyFlowers(Math.min(space, 8));
        let n = 0;
        for (const b of batches) {
          for (let i = 0; i < b.count; i++) {
            this.storeroom.push(b.kind);
            n += 1;
          }
        }
        if (n > 0) {
          this.storeCooldown = 0.18;
          this.refreshStoreroomVisual();
          this.audio.play('deposit');
          this.flashStatus(`Stored ${n} flowers for clerks`);
          this.onTutorial(5, 'Clerks auto-serve the queue from stock');
        }
      }
    }

    // Upgrade kiosk proximity
    this.nearUpgrade = p.distanceTo(this.world.upgradePos) < 1.6;
    if (this.nearUpgrade) {
      this.flashStatus('Upgrade kiosk — press U or tap ⬆️');
    }

    // Direct sell
    for (const c of this.customers) {
      if (c.state !== 'queue' && c.state !== 'enter') continue;
      if (p.distanceTo(c.position) > GAME.sellRadius) continue;
      if (this.playerStack.counts()[c.want] <= 0) continue;
      if (this.playerStack.take(c.want, 1) > 0) this.completeSale(c, 'player');
    }
  }

  private completeSale(c: Customer, by: 'player' | 'clerk'): void {
    const price = salePrice(c.want, this.stats.pricesLevel);
    // Township-style: drop cash piles instead of instant bank for player satisfaction
    this.spawnCashPile(price, this.world.moneyPad);
    // small instant tip
    const tip = Math.max(0, Math.floor(price * 0.15));
    if (tip > 0) this.cash += tip;
    this.audio.play('sell');
    c.state = 'leave';
    c.bubble.visible = false;
    this.flashStatus(
      by === 'player'
        ? `Sold ${FLOWER_META[c.want].label} · cash on pad +$${price}`
        : `Clerk sold ${FLOWER_META[c.want].label} · +$${price} on pad`,
    );
    if (tip) this.hud.floatCash(tip);
    this.checkMilestones();
    this.onTutorial(6, 'Pick up green cash stacks, then upgrade the shop');
    this.persist();
  }

  private checkMilestones(): void {
    while (true) {
      const goal = this.currentGoal();
      if (this.cash < goal) break;
      if (this.milestoneIndex < GAME.milestones.length) this.milestoneIndex += 1;
      else this.milestoneIndex += 1;
      this.audio.play('milestone');
      this.hud.showMilestone(`🌸 Shop Goal $${goal}! Keep expanding`);
      this.flashStatus(`Milestone cleared · next $${this.currentGoal()}`);
      if (this.milestoneIndex > 40) break;
    }
  }

  private currentGoal(): number {
    if (this.milestoneIndex < GAME.milestones.length) return GAME.milestones[this.milestoneIndex]!;
    const last = GAME.milestones[GAME.milestones.length - 1]!;
    return last + (this.milestoneIndex - GAME.milestones.length + 1) * 15000;
  }

  private updateGuide(): void {
    let target = this.world.counterPos;
    const counts = this.playerStack.counts();
    const flowers = FLOWER_ORDER.reduce((s, k) => s + counts[k], 0);
    const hasOrder = this.customers.some(
      (c) => (c.state === 'queue' || c.state === 'enter') && counts[c.want] > 0,
    );
    if (counts.honey > 0) target = this.world.hivePos;
    else if (this.cashPiles.some((p) => p.alive)) target = this.world.moneyPad;
    else if (hasOrder) {
      const c = this.customers.find(
        (cust) => (cust.state === 'queue' || cust.state === 'enter') && counts[cust.want] > 0,
      );
      if (c) target = c.position;
    } else if (flowers > 8) target = this.world.storeroomPos;
    else if (this.cash >= 40 && this.levels.stack === 0) target = this.world.upgradePos;
    else {
      const plot = this.world.plots.find((p) => p.ready && !p.locked);
      if (plot) target = plot.position;
    }

    const from = this.player.position;
    const mid = from.clone().lerp(target, 0.35);
    mid.y = 0.05;
    this.guide.position.copy(mid);
    const dx = target.x - from.x;
    const dz = target.z - from.z;
    this.guide.rotation.y = Math.atan2(dx, dz);
    this.guide.visible = from.distanceTo(target) > 2.5;
    this.guide.position.y = 0.08 + Math.sin(this.elapsed * 4) * 0.03;
  }

  private refreshStoreroomVisual(): void {
    while (this.storeVisual.children.length) this.storeVisual.remove(this.storeVisual.children[0]!);
    const counts = emptyCounts();
    for (const k of this.storeroom) {
      if (k === 'honey' || k === 'cash') continue;
      counts[k] += 1;
    }
    let col = 0;
    for (const kind of FLOWER_ORDER) {
      const n = Math.min(counts[kind], 10);
      for (let j = 0; j < n; j++) {
        const m = createCarryItemMesh(kind);
        m.position.set(-0.7 + col * 0.42, 0.22 + j * 0.2, 0.15);
        this.storeVisual.add(m);
      }
      if (n > 0) col += 1;
    }
  }

  private syncHud(): void {
    this.hud.setCash(this.cash, this.currentGoal(), this.milestoneIndex);
    this.hud.setStack(this.playerStack.count, this.stats.stackMax, this.playerStack.counts());
    if (this.hud.isUpgradeOpen()) this.hud.renderUpgrades(this.levels, this.cash);
  }

  private flashStatus(text: string, deny = false): void {
    this.hud.setStatus(text);
    if (deny) this.audio.play('deny');
  }

  private onTutorial(step: number, tip: string | null): void {
    if (step > this.tutorialStep) {
      this.tutorialStep = step;
      if (tip) this.hud.showTip(tip, 4.2);
    }
  }

  private moveToward(pos: THREE.Vector3, dest: THREE.Vector3, speed: number, dt: number): void {
    const delta = dest.clone().sub(pos);
    const dist = delta.length();
    if (dist < 0.001) return;
    pos.addScaledVector(delta.normalize(), Math.min(dist, speed * dt));
  }

  private face(mesh: THREE.Object3D, dest: THREE.Vector3, from: THREE.Vector3): void {
    const dx = dest.x - from.x;
    const dz = dest.z - from.z;
    if (Math.hypot(dx, dz) > 0.01) mesh.rotation.y = Math.atan2(dx, dz);
  }

  private render(): void {
    resizeRenderer(this.renderer, this.camera, this.maxDpr);
    this.renderer.render(this.scene, this.camera);
  }

  purchaseUpgrade(id: UpgradeId): void {
    if (!canBuy(id, this.levels, this.cash)) {
      this.audio.play('deny');
      return;
    }
    const cost = costFor(id, this.levels[id]);
    this.cash -= cost;
    const fromLevel = this.levels[id];
    this.levels[id] += 1;
    const preview = effectPreview(id, fromLevel);
    this.applyStatsToActors();
    this.audio.play('milestone');
    this.hud.showMilestone(`⬆️ ${preview}`);
    if (id === 'fields') {
      this.flashStatus('New garden ring unlocked — head south into the fields!');
      this.hud.showTip('Walk into the newly grown bushes — the world just expanded', 4.5);
    } else if (id === 'stack') {
      this.flashStatus(`Basket expanded — stack cap ${this.stats.stackMax}`);
    } else if (id === 'flowers') {
      this.flashStatus('New flower species added to your catalog!');
    } else {
      this.flashStatus(`Upgrade complete · ${preview}`);
    }
    this.hud.renderUpgrades(this.levels, this.cash);
    this.syncHud();
    this.persist();
    this.onTutorial(8, null);
  }

  private installTestHooks(): void {
    window.__THREE_GAME_TEST_HOOKS__ = {
      seed: (value: number) => {
        this.rng = createSeededRandom(value);
      },
      setState: (name: string) => {
        if (name === 'active-play' || name === 'complete') {
          if (!this.playing) this.beginPlay();
        }
      },
      setPausedForScreenshot: (paused: boolean) => {
        this.pausedForScreenshot = paused;
      },
      setReducedMotion: () => {},
      hideDebugUi: () => {},
    };
  }

  private publishDiagnostics(): void {
    const info = this.renderer.info;
    const canvas = this.renderer.domElement;
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.frame,
      elapsed: this.elapsed,
      score: this.cash,
      targetScore: this.currentGoal(),
      complete: false,
      player: {
        position: {
          x: this.player.position.x,
          y: this.player.position.y,
          z: this.player.position.z,
        },
        speed: this.velocity.length(),
      },
      renderer: {
        calls: info.render.calls,
        triangles: info.render.triangles,
        geometries: info.memory.geometries,
        textures: info.memory.textures,
      },
      canvas: {
        clientWidth: canvas.clientWidth,
        clientHeight: canvas.clientHeight,
        width: canvas.width,
        height: canvas.height,
        dpr: this.renderer.getPixelRatio(),
      },
    };
  }
}

function mustEl<T extends HTMLElement = HTMLElement>(sel: string): T {
  const n = document.querySelector<T>(sel);
  if (!n) throw new Error(`Missing ${sel}`);
  return n;
}
